/**
 * Error codes for the Auth plugin
 */
export var AuthPluginErrorCode;
(function (AuthPluginErrorCode) {
    /**
     * The operation failed due to no network connection
     */
    AuthPluginErrorCode["networkNotConnected"] = "networkNotConnected";
    /**
     * The operation failed because it would require network access but allowNetworkAccess is set to false
     */
    AuthPluginErrorCode["networkNotAllowed"] = "networkNotAllowed";
    /**
     * The operation failed because it would require user interaction but allowUserInteraction is set to false
     */
    AuthPluginErrorCode["userInteractionNotAllowed"] = "userInteractionNotAllowed";
    /**
     * The operation was canceled by the user
     */
    AuthPluginErrorCode["userCanceled"] = "userCanceled";
    /**
     * The given account is not logged in
     */
    AuthPluginErrorCode["accountNotLoggedIn"] = "accountNotLoggedIn";
    /**
     * The token request process failed.  This error is only returned if a token refresh request fails, and
     * the failure is NOT due to an invalid refresh token.  If the failure happened due to an invalid
     * refresh token, then depending on the value of allowUserInteraction option, either the sign in flow will
     * start or the 'userInteractionNotAllowed' error will be returned
     */
    AuthPluginErrorCode["tokenRequestFailed"] = "tokenRequestFailed";
    /**
     * The logout request process failed
     */
    AuthPluginErrorCode["logoutRequestFailed"] = "logoutRequestFailed";
    /**
     * An invalid option was specified
     */
    AuthPluginErrorCode["invalidOption"] = "invalidOption";
    /**
     * The operation failed due to an unknown error
     */
    AuthPluginErrorCode["unknownError"] = "unknownError";
    /**
     * The attempt to cancel the operation failed.
     */
    AuthPluginErrorCode["cancellationError"] = "cancellationError";
    /**
     * No successful aggregate api requests for permission values has occurred.
     */
    AuthPluginErrorCode["noPermissionsObtained"] = "noPermissionsObtained";
    /**
    * Native app failed to provide configuration permissions.
    */
    AuthPluginErrorCode["failedToProvideConfigurationPermissions"] = "failedToProvideConfigurationPermissions";
    /**
     * Plugin does not support requested operation.
     */
    AuthPluginErrorCode["unsupportedOperation"] = "unsupportedOperation";
    /**
     * Returned by IDP lookup methods when the feature is disabled. Check status with
     * {@linkcode AuthPlugin.isIdentityProviderLookupEnabled} before performing lookup.
     */
    AuthPluginErrorCode["idpLookupDisabled"] = "idpLookupDisabled";
    /**
    * An identity provider lookup call failed due to a network, service, or response parsing error.
    */
    AuthPluginErrorCode["idpLookupFailed"] = "idpLookupFailed";
})(AuthPluginErrorCode || (AuthPluginErrorCode = {}));
export var TokenState;
(function (TokenState) {
    TokenState["loginStart"] = "loginStart";
    TokenState["loginFinish"] = "loginFinish";
    TokenState["refreshStart"] = "refreshStart";
    TokenState["refreshFinish"] = "refreshFinish";
    TokenState["exchangeStart"] = "exchangeStart";
    TokenState["exchangeFinish"] = "exchangeFinish";
    TokenState["logoutStart"] = "logoutStart";
    TokenState["logoutFinish"] = "logoutFinish";
})(TokenState || (TokenState = {}));
/**
 * Controls which identifiers are used when looking up identity provider configurations.
 *
 * @see {@linkcode AuthPlugin.getIdentityProviderConfigurations}
 */
export var IdentityProviderLookupTarget;
(function (IdentityProviderLookupTarget) {
    /**
     * Query using the full email address.
     */
    IdentityProviderLookupTarget["emailOnly"] = "emailOnly";
    /**
     * Query using the domain, extracted automatically from `email`.
     */
    IdentityProviderLookupTarget["domainOnly"] = "domainOnly";
    /**
     * Query both the full email and domain in parallel. Duplicate UUIDs are consolidated into
     * a single {@linkcode IdentityProviderConfiguration} with both origins in its `origin` array.
     */
    IdentityProviderLookupTarget["emailAndDomain"] = "emailAndDomain";
})(IdentityProviderLookupTarget || (IdentityProviderLookupTarget = {}));
/**
 * Indicates which lookup(s) produced an {@linkcode IdentityProviderConfiguration}.
 *
 * @see {@linkcode IdentityProviderConfiguration.origin}
 */
export var IdentityProviderLookupOrigin;
(function (IdentityProviderLookupOrigin) {
    /**
     * The configuration was discovered via the full email address.
     */
    IdentityProviderLookupOrigin["email"] = "email";
    /**
     * The configuration was discovered via the email domain.
     */
    IdentityProviderLookupOrigin["domain"] = "domain";
})(IdentityProviderLookupOrigin || (IdentityProviderLookupOrigin = {}));
export var TokenType;
(function (TokenType) {
    /**
     * For user token
     */
    TokenType["user"] = "user";
    /**
     * For device token
     */
    TokenType["device"] = "device";
})(TokenType || (TokenType = {}));
/**
 * Token Lifetime Requirement types
 */
export var TokenLifetimeRequirementType;
(function (TokenLifetimeRequirementType) {
    /**
     * Requires a token that will attempt to refresh after X seconds since its `iat` time.
     *
     * Specifying `timeInterval > (exp - iat)` will be ignored and the component will refresh
     * when the token expires.
     */
    TokenLifetimeRequirementType["preferredMaximumSecondsSinceIssued"] = "preferredMaximumSecondsSinceIssued";
    /**
     * Requires a token that will not expire for at least X seconds.
     *
     * Specifying `timeInterval >= (exp - iat)` will always trigger token refresh.
     */
    TokenLifetimeRequirementType["preferredMinimumSecondsUntilExpiration"] = "preferredMinimumSecondsUntilExpiration";
})(TokenLifetimeRequirementType || (TokenLifetimeRequirementType = {}));
/**
 * User interaction start points
 */
export var UserInteractionEntryPoint;
(function (UserInteractionEntryPoint) {
    /**
     * User interaction should start on the sign in page
     */
    UserInteractionEntryPoint["signIn"] = "signIn";
    /**
     * User interaction should start on the create-account page
     */
    UserInteractionEntryPoint["createAccount"] = "createAccount";
})(UserInteractionEntryPoint || (UserInteractionEntryPoint = {}));
/**
 * Authentication provider types
 */
export var ProviderType;
(function (ProviderType) {
    /**
     * Stratus authentication provider
     */
    ProviderType["stratus"] = "stratus";
    /**
     * One Cloud authentication provider
     */
    ProviderType["oneCloud"] = "oneCloud";
})(ProviderType || (ProviderType = {}));
//# sourceMappingURL=auth.js.map